import "./PromoModals.css";
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TextField, Chip } from '@mui/material';
import { AppDispatch, RootState } from '../../../redux/store';
import { updatePromo, fetchPromoById } from "../../../redux/actions/promos.actions";
import { fetchDealerships } from "../../../redux/actions/dealership.actions";
import { ModalProps } from "../../../components/common/Interfaces/Modals";
import usePermissionCheck from "../../../hooks/usePermissionCheck";
import { uploadImage } from "../../../services/api/images.api";
import PromoPreviewModal from "./PromoPreviewModal";
import ModalShell from "../../../components/common/Modals/ModalShell";
import { CloseIcon } from "../../../components/common/Icons/Icons";
import { clearError } from "../../../redux/reducers/promos.reducer";

const UpdatePromoModal: React.FC<ModalProps> = ({open, setOpen, object}) => {
    const promo = object;

    const handleClose = () => {
      handleReset();
      setOpen(false);
    };

    const handleReset = () => {
      dispatch(clearError());
      setSubmitted(false);
    };

    const dispatch = useDispatch<AppDispatch>();
    const { accessToken, data: user } = useSelector((state: RootState) => state.user);
    const { error, loading } = useSelector((state: RootState) => state.promos);
    const { data: dealerships, loading: dealershipsLoading, meta: dealershipsMeta } = useSelector((state: RootState) => state.dealerships);

    const canSendToAll = user?.permissions?.includes('can_send_promos_to_all');
    const { canAccess: canViewDealerships } = usePermissionCheck(['view_dealerships']);

    const [submitted, setSubmitted] = useState(false);
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [url, setUrl] = useState('');
    const [buttonLabel, setButtonLabel] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [frequency, setFrequency] = useState('');
    const [previewOpen, setPreviewOpen] = useState(false);

    // Image upload
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
      }
    };

    // Dealership selection
    const [selectedDealerships, setSelectedDealerships] = useState<{id: number, name: string}[]>([]);
    const [dealershipInput, setDealershipInput] = useState('');
    const [dealershipPage, setDealershipPage] = useState(1);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (open && promo) {
        setTitle(promo.title ?? '');
        setBody(promo.body ?? '');
        setUrl(promo.url ?? '');
        setButtonLabel(promo.button_label ?? '');
        setStartDate(promo.start_date ? promo.start_date.split('T')[0] : '');
        setEndDate(promo.end_date ? promo.end_date.split('T')[0] : '');
        setFrequency(promo.frequency != null ? String(promo.frequency) : '7');
        setSubmitted(false);
        setImageFile(null);
        setImagePreview(promo.image_url ?? null);
        setUploading(false);
        setSelectedDealerships([]);
        setDealershipInput('');
        setDealershipPage(1);
        setDropdownOpen(false);

        // Fetch full promo to get dealerships
        dispatch(fetchPromoById(promo.id))
          .unwrap()
          .then((data) => {
            if (data.dealerships) {
              setSelectedDealerships(data.dealerships.map((d: any) => ({ id: d.id, name: d.name })));
            }
          })
          .catch(() => {});
      }
    }, [open, promo, dispatch]);

    // Fetch dealerships for dropdown
    useEffect(() => {
      if (canViewDealerships && dropdownOpen) {
        const query = `name=${encodeURIComponent(dealershipInput)}&page=${dealershipPage}`;
        dispatch(fetchDealerships(query));
      }
    }, [dealershipInput, dealershipPage, dropdownOpen, canViewDealerships, dispatch]);

    // Close dropdown on outside click
    useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
        if (
          inputRef.current &&
          !inputRef.current.contains(event.target as Node) &&
          !(event.target as HTMLElement).closest('.dealership-dropdown')
        ) {
          setDropdownOpen(false);
        }
      }
      if (dropdownOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      } else {
        document.removeEventListener('mousedown', handleClickOutside);
      }
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [dropdownOpen]);

    const handleSelectDealership = (d: any) => {
      if (!selectedDealerships.find(s => s.id === d.id)) {
        setSelectedDealerships([...selectedDealerships, { id: d.id, name: d.name }]);
      }
      setDealershipInput('');
      setDropdownOpen(false);
    };

    const handleRemoveDealership = (id: number) => {
      setSelectedDealerships(selectedDealerships.filter(d => d.id !== id));
    };

    const handleSubmit = async (e: React.FormEvent, asDraft: boolean) => {
      e.preventDefault();
      setUploading(true);

      let imageKey: string | null | undefined = undefined;
      try {
        if (imageFile) {
          imageKey = await uploadImage(accessToken, imageFile, 'promos');
        } else if (!imagePreview) {
          imageKey = null; // image was removed
        }
      } catch (_) {
        setUploading(false);
        return;
      }

      setUploading(false);
      setSubmitted(true);
      await dispatch(updatePromo({
        id: promo.id,
        data: {
          title,
          body,
          ...(imageKey !== undefined && { image_key: imageKey }),
          url: url || null,
          button_label: buttonLabel || null,
          start_date: startDate || null,
          end_date: endDate || null,
          frequency: frequency ? Number(frequency) : null,
          is_draft: asDraft,
          dealer_ids: selectedDealerships.map(d => d.id),
        },
      }));
    };

  const editing = !submitted || loading || uploading;
  const saving = loading || uploading;
  const saveDisabled = loading || uploading || !accessToken || !title || !body || !startDate || !endDate || (canViewDealerships && canSendToAll && selectedDealerships.length === 0);
  const validDealerships = (dealerships || []).filter((d: any) => d && d.id && d.name);

  return (
    <>
    <ModalShell
      open={open}
      onClose={handleClose}
      size={editing ? "md" : "sm"}
      busy={saving}
      tone={editing ? undefined : error ? "error" : "success"}
      title={editing ? <span className="promo-title-row">
          Edit promo
          {!!promo?.is_draft && <span className="chip promo-status draft">Draft</span>}
        </span> : error ? "An error occurred" : "Promo updated"}
      actions={editing ?
        <>
          <button className="btn dlg-actions-start" disabled={!title || !body} onClick={() => setPreviewOpen(true)}>Preview</button>
          <button className="btn" onClick={handleClose} disabled={saving}>Cancel</button>
          <button className="btn" disabled={saveDisabled} onClick={(e) => handleSubmit(e, true)}>
            {loading ? <span className="spinner promo-btn-spinner" aria-label="Saving"/> : (promo?.is_draft ? "Save as draft" : "Convert to draft")}
          </button>
          <button className="btn btn-primary" disabled={saveDisabled} onClick={(e) => handleSubmit(e, false)}>
            {loading ? <span className="spinner promo-btn-spinner" aria-label="Saving"/> : (promo?.is_draft ? "Publish" : "Publish changes")}
          </button>
        </>
        : error ?
        <>
          <button className="btn" onClick={handleClose}>Close</button>
          <button className="btn btn-primary" onClick={handleReset}>Try again</button>
        </>
        :
        <button className="btn btn-primary" onClick={handleClose}>OK</button>
      }
    >
      {!editing ? (
        error ? <p>{error}</p> : <p>Promo successfully updated.</p>
      ) : (
        <form onSubmit={(e) => e.preventDefault()} className="promo-form">
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            disabled={loading}
            size="small"
            fullWidth
          />
          <TextField
            label="Body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            multiline
            minRows={3}
            required
            disabled={loading}
            size="small"
            fullWidth
          />
          <div className="promo-schedule">
            <TextField
              label="Start date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={loading}
              size="small"
              required
              fullWidth
            />
            <TextField
              label="End date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={loading}
              size="small"
              required
              fullWidth
            />
            <div className="promo-repeat">
              <TextField
                label="Repeat every"
                type="number"
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                disabled={loading}
                size="small"
                inputProps={{ min: 1 }}
              />
              <span>days</span>
            </div>
          </div>
          <TextField
            label="URL (optional)"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={loading}
            size="small"
            fullWidth
          />
          <TextField
            label="Button label (optional)"
            value={buttonLabel}
            onChange={(e) => setButtonLabel(e.target.value)}
            disabled={loading}
            size="small"
            placeholder="e.g. Learn More"
            fullWidth
          />

          {canViewDealerships && canSendToAll ? (
            <div className="dlg-section">
              <div className="promo-dealer-search">
                <TextField
                  inputRef={inputRef}
                  label="Search dealerships"
                  variant="outlined"
                  size="small"
                  value={dealershipInput}
                  onFocus={() => setDropdownOpen(true)}
                  onChange={e => {
                    setDealershipInput(e.target.value);
                    setDealershipPage(1);
                    if (e.target.value !== '') {
                      setDropdownOpen(true);
                    }
                  }}
                  autoComplete="off"
                  fullWidth
                  disabled={loading}
                />
                {dropdownOpen && (
                  <div className="dealership-dropdown promo-dealer-dropdown">
                    {dealershipsLoading ? (
                      <div className="promo-dealer-empty">Loading…</div>
                    ) : (
                      <>
                        {validDealerships.length === 0 ? (
                          <div className="promo-dealer-empty">No dealerships found</div>
                        ) : (
                          validDealerships.map((d: any) => (
                            <button
                              type="button"
                              key={d.id}
                              className={`promo-dealer-option${selectedDealerships.find(s => s.id === d.id) ? ' selected' : ''}`}
                              onClick={() => handleSelectDealership(d)}
                            >
                              {d.name}
                            </button>
                          ))
                        )}
                        <div className="promo-dealer-pager">
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            disabled={dealershipPage <= 1}
                            onClick={() => dealershipPage > 1 && setDealershipPage(dealershipPage - 1)}
                          >
                            Previous
                          </button>
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            disabled={!(dealershipsMeta && dealershipPage < dealershipsMeta.total_pages)}
                            onClick={() => dealershipsMeta && dealershipPage < dealershipsMeta.total_pages && setDealershipPage(dealershipPage + 1)}
                          >
                            Next
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
              {selectedDealerships.length > 0 && (
                <div className="promo-chips">
                  {selectedDealerships.map(d => (
                    <Chip
                      key={d.id}
                      label={d.name}
                      size="small"
                      onDelete={() => handleRemoveDealership(d.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <TextField
              label="Dealership"
              value={selectedDealerships.map(d => d.name).join(', ') || (user?.dealership?.name ?? `Dealership #${user?.dealership_id}`)}
              disabled
              size="small"
              fullWidth
            />
          )}
          <div className="promo-upload">
            <label className={`btn promo-upload-btn${loading || uploading ? ' disabled' : ''}`}>
              {imageFile || imagePreview ? 'Change image' : 'Upload image (optional)'}
              <input type="file" hidden accept="image/*" onChange={handleImageChange} disabled={loading || uploading}/>
            </label>
            {imagePreview && (
              <div className="promo-thumb">
                <img src={imagePreview} alt="Preview"/>
                <button type="button" className="promo-thumb-remove" aria-label="Remove image" onClick={() => { setImageFile(null); setImagePreview(null); }}>
                  <CloseIcon/>
                </button>
              </div>
            )}
          </div>
        </form>
      )}
    </ModalShell>
    <PromoPreviewModal
      open={previewOpen}
      onClose={() => setPreviewOpen(false)}
      promo={{ title, body, image_url: imagePreview, url, button_label: buttonLabel }}
    />
    </>
  );
};

export default UpdatePromoModal;
